import { Variant, Product, VariantServices } from "../../types/types";
import { Smush } from "../../types/helpers";
import {
  generateBreadcrumbs,
  generateDeliveryOptions,
  generatePageName,
  generatePromotions,
  generatePromotionClassifications,
} from "./helpers";
import { ensureIsActive } from "../product/ensureIsActive";
import { getDefaultCategory } from "../product/getDefaultCategory";
import { logger } from "@/utils/logger";

type JLDataType = {
  recsModel: Record<string, never>;
  page: {
    locale: {
      country: "uk";
      currency: "gbp";
      language: "en-gb";
    };
    pageInfo: {
      breadCrumb: {
        clickable: number;
        value: string;
      }[];
      pageCanonicalURL: string;
      pageName: string;
      pageType: string;
      pageConfigName: string;
      site: "johnlewis.com";
    };
    productPages: {
      asaf: Record<string, never>;
      brand: string;
      category: string;
      colour: string;
      content: Record<string, never>;
      deliveryOptions: string;
      displayedSku: string;
      numberOfAvailableProductSkus: number;
      numberOfAvailableProductSkusDetails: string[];
      numberOfProductSkus: number;
      outOfStockProductSkus: number;
      outOfStockProductSkusDetails: string[];
      price: string;
      productId: string;
      productName: string;
      productLifecycle: "retired product" | "";
      productViewType: "standard product" | "main product";
      promotionClassifications: {
        group: string;
        type: string;
      }[];
      promotions: string;
      reviews: {
        averageRating: number;
        totalNumber: number;
      };
      services: VariantServices;
      size: string;
    };
  };
};

function filterAvailability(variants: Variant[], isAvailable = false) {
  return variants
    .filter(
      (variant) => !!variant.availability?.availableToOrder === isAvailable,
    )
    .map((variant) => variant.aliases.skuId);
}

export const generatePageLoadAnalyticsDataLayer = (
  product: Smush<Product>,
  selectedVariant: Variant,
  pageConfigName?: string,
): JLDataType => {
  const variants: Variant[] = product.variants;
  const availableProductSkus = filterAvailability(variants, true);
  const outOfStockProductSkus = filterAvailability(variants, false);
  /**
   * Added to debug Unspecified page config
   * See ticket for more info: https://www.jlpit.com/jira/browse/WA-788
   */
  if (!pageConfigName) {
    logger("warn", `PageConfigName: ${pageConfigName}`);
  }

  return {
    recsModel: {},
    page: {
      locale: {
        country: "uk",
        currency: "gbp",
        language: "en-gb",
      },
      pageInfo: {
        breadCrumb: generateBreadcrumbs(product),
        pageCanonicalURL: `${window.location.protocol}//${window.location.host}${product.canonicalURL}`,
        pageName: generatePageName(product),
        pageType: `pdp scaffold product`,
        pageConfigName: `${pageConfigName || ""}`,
        site: "johnlewis.com",
      },
      productPages: {
        asaf: {},
        brand: product.brand?.name || "",
        category: getDefaultCategory(product) || "",
        colour: selectedVariant.differentiators?.colour || "",
        content: {},
        deliveryOptions: generateDeliveryOptions(product),
        displayedSku: selectedVariant.aliases.skuId,
        numberOfAvailableProductSkus: availableProductSkus.length,
        numberOfAvailableProductSkusDetails: availableProductSkus,
        numberOfProductSkus: variants.length,
        outOfStockProductSkus: outOfStockProductSkus.length,
        outOfStockProductSkusDetails: outOfStockProductSkus,
        price: selectedVariant.price?.value || "",
        productId: product.aliases.pimProductId,
        productName: product.title,
        productLifecycle: ensureIsActive(product) ? "" : "retired product",
        productViewType: "main product",
        promotionClassifications: generatePromotionClassifications(
          product,
          selectedVariant,
        ),
        promotions: generatePromotions(product),
        reviews: product.reviewSummary
          ? {
              averageRating: product.reviewSummary.averageRating,
              totalNumber: product.reviewSummary.numberOfReviews,
            }
          : {
              averageRating: 0,
              totalNumber: 0,
            },
        services: selectedVariant.services || [],
        size: selectedVariant.differentiators?.size || "",
      },
    },
  };
};
